import { Inject, Vue, Watch, Provide } from 'vue-property-decorator';
import {
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_SERVICE,
  DATADOG_ENV,
  DATADOG_VERSION,
  DATADOG_SESSION_SAMPLE_RATE,
  DATADOG_SESSION_REPLAY_SAMPLE_RATE,
  DEFAULT_LANGUAGE,
  MINIO_BUCKET_ADDRESS,
  LOCAL_COUNTRY
} from '@/constants';
import Component from 'vue-class-component';
import axios from 'axios';
import _ from 'lodash';
import JhiFooter from '@/core/jhi-footer/jhi-footer.vue';
import JhiNavbar from '@/core/jhi-navbar/jhi-navbar.vue';
import Maintenance from '@/core/maintenance/maintenance.vue';
import News from '@/core/news/news.vue';
import Ribbon from '@/core/ribbon/ribbon.vue';
import LanguageCode from '@/locale/language-code';
import KeywordsCMSModel from '@/visa-application/models/KeywordsCMSModel';
import DirectusService from '@/core/directus/directus.service';
import LoginService from '@/account/login.service';
import ApplicationCenterCMSModel from '@/visa-application/models/ApplicationCenterCMSModel';
import { initPiwik } from './piwik-tracking';
import { getLocalStorage } from './shared/utils/utils';
import { datadogRum } from '@datadog/browser-rum';

const keywords = require('../../resources/i18n/pageContent/keywords.json');
@Component({
  components: {
    'jhi-footer': JhiFooter,
    'jhi-navbar': JhiNavbar,
    news: News,
    ribbon: Ribbon,
    Maintenance
  }
})
export default class App extends Vue {
  @Inject('directusService')
  private directusService: () => DirectusService;
  @Inject('loginService') private loginService: () => LoginService;

  public getApplicationCentersPromise: Promise<any>;
  public reversedLanguages: LanguageCode[] = [LanguageCode.AR];
  public specialGlyphsLanguages: LanguageCode[] = [LanguageCode.VI];
  public isRTL = false;
  public isSpecialGlyphs = false;
  public requestInProcess = 0;
  public isLoading = false;
  public isShowDoubleUp = false;
  public back_to_the_top = '';
  public MINIO_BUCKET_ADDRESS = MINIO_BUCKET_ADDRESS;
  public showForbiddenAccessPopup = false;

  private currentLanguage: LanguageCode = this.$store.getters.currentLanguage;

  public async mounted(): Promise<void> {
    initPiwik(process.env.PIWIK_PROPERTY_ID);

    await this.retrieveKeywordsFromCMS();
    this.axiosSpinner();
    window.addEventListener('scroll', this.isShowDoubleUpButton, true);
    this.isRTL = this.reversedLanguages.includes(this.currentLanguage);
    this.isSpecialGlyphs = this.specialGlyphsLanguages.includes(this.currentLanguage);
    // Check if the center is enabled for login
    await this.getApplicationCentersPromise;
    const applicationCenter = _.find(this.$store.getters.applicationCenter, { code: _.get(this.$route.params, 'issuer', undefined) });

    // regex patterns to identify known bot instances:
    let botPattern = "(googlebot\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)";
    let regex = new RegExp(botPattern, 'i');

    // define var conditionalSampleRate as 0 if the userAgent matches a pattern in botPatterns
    // otherwise, define conditionalSampleRate as 100
    let conditionalSampleRate = regex.test(navigator.userAgent) ? 0 : DATADOG_SESSION_SAMPLE_RATE;

    if (DATADOG_APPLICATION_ID !== 'disabled') {
      datadogRum.init({
        applicationId: DATADOG_APPLICATION_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.eu',
        service: DATADOG_SERVICE,
        env: DATADOG_ENV,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: DATADOG_VERSION,
        sessionSampleRate: conditionalSampleRate,
        sessionReplaySampleRate: DATADOG_SESSION_REPLAY_SAMPLE_RATE,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [
          /https:\/\/.*\.tlscontact\.com/,
          /https:\/\/tlscontact-team.*\.directus\.app/
        ]
      });

      datadogRum.setGlobalContextProperty('country', LOCAL_COUNTRY);
    }

    if (this.$store.getters.authenticated) {
      if (applicationCenter && !_.get(applicationCenter, 'open_login')) {
        this.logout();
        return;
      }

      this.hidrateBasketStatus();
    }
  }

  @Watch('$route')
  routechange(to: any) {
    const routelink: string = to.path;
    const footer = document.getElementById('switch');
    const footers = document.body.clientWidth - 12;
    if (routelink.includes('personal') && this.$store.getters.authenticated === true) {
      footer.style.display = 'inherit';
    } else if (routelink.includes('personal') === false && this.$store.getters.authenticated === true && footers < 450) {
      footer.style.display = 'none';
    }

    // add ua2pl Chat Widget integration
    const isInquiryAndUA2PL = window.location.href.match(/inquiry\/ua\/ua[A-Z]{3}2pl/);
    const element = document.getElementById('bwc-wrap');
    if (isInquiryAndUA2PL && !element) {
      console.log('add element');
      const widgetHash = 'RX2xbzus5yMkrOvOAvEN';
      const bch = document.createElement('script');
      bch.type = 'text/javascript';
      bch.async = true;
      bch.src = '//widgets.binotel.com/chat/widgets/' + widgetHash + '.js';
      const sn = document.getElementsByTagName('script')[0];
      setTimeout(function() {
        sn.parentNode.insertBefore(bch, sn);
      }, 2000);
    } else if (isInquiryAndUA2PL && element) {
      element.style.display = 'block';
    } else if (element) {
      element.style.display = 'none';
    }
  }

  @Watch('$store.getters.currentLanguage', { immediate: true, deep: true })
  public onCurrentLanguageUpdated(newLanguage: LanguageCode): void {
    this.isRTL = this.reversedLanguages.includes(newLanguage);
    this.isSpecialGlyphs = this.specialGlyphsLanguages.includes(newLanguage);
    this.currentLanguage = newLanguage;
    this.retrieveKeywordsFromCMS();
  }

  public hidrateBasketStatus() {
    const skipPay = getLocalStorage('skipPay');
    this.$store.commit('setSkipPay', skipPay === 'true');
  }

  public isShowDoubleUpButton() {
    if (document.body.scrollTop > screen.height / 2 || document.documentElement.scrollTop > screen.height / 2) {
      this.isShowDoubleUp = true;
    } else {
      this.isShowDoubleUp = false;
    }
  }

  public topFunction() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  public async retrieveKeywordsFromCMS(): Promise<void> {
    this.$store.commit('keywords', { ...keywords[DEFAULT_LANGUAGE], ...keywords[this.currentLanguage] });

    this.getApplicationCentersPromise = this.directusService().getCollection<ApplicationCenterCMSModel>(
      ApplicationCenterCMSModel,
      'application_centers',
      ['*.*'],
      undefined,
      this.currentLanguage
    );
    this.getApplicationCentersPromise
      .then((result: ApplicationCenterCMSModel[]) => {
        this.$store.commit('applicationCenter', result);
      })
      .catch(error => console.error(error));
  }

  private axiosSpinner() {
    axios.interceptors.request.use(
      config => {
        this.requestInProcess += 1;
        this.isLoading = this.requestInProcess > 0;
        return config;
      },
      error => {
        this.requestInProcess -= 1;
        this.isLoading = this.requestInProcess > 0;
        Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      response => {
        this.requestInProcess -= 1;
        this.isLoading = this.requestInProcess > 0;
        return response;
      },
      error => {
        const { status, headers } = error.response;

        if (status === 401 && this.$route.meta.authorities !== undefined && this.$route.meta.authorities.length > 0) {
          sessionStorage.setItem('requested-url', this.$route.fullPath);
          this.loginService().goToLogin('', '');
        }

        // Display cloudflare popup
        if (headers.server && headers.server.toLowerCase() === 'cloudflare') this.showForbiddenAccessPopup = [403, 429].includes(status);

        this.requestInProcess -= 1;
        this.isLoading = this.requestInProcess > 0;
        return Promise.reject(error);
      }
    );
  }

  public logout(): void {
    this.loginService()
      .logout()
      .then(response => {
        this.$store.commit('logout');
        const data = response.data;
        let logoutUrl = data.logoutUrl;
        // if Keycloak, uri has protocol/openid-connect/token
        if (logoutUrl.indexOf('/protocol') > -1) {
          logoutUrl =
            logoutUrl +
            '?redirect_uri=' +
            window.location.origin +
            '/visa/' +
            _.get(this.$route.params, 'residenceCountryCode') +
            '/' +
            _.get(this.$route.params, 'issuer') +
            '/home';
        } else {
          // Okta
          logoutUrl =
            logoutUrl +
            '?id_token_hint=' +
            data.idToken +
            '&post_logout_redirect_uri=' +
            window.location.origin +
            '/visa/' +
            _.get(this.$route.params, 'residenceCountryCode') +
            '/' +
            _.get(this.$route.params, 'issuer') +
            '/home';
        }
        window.location.href = logoutUrl;
      });
  }
}
